import { Alert, Button, Group, Loader, Paper, SegmentedControl, Stack, Text, Textarea, UnstyledButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AssignableUser, CommentMention, CommentVisibility } from "@msc/shared";

import { isJiraReauthRequired } from "../../hooks/useCustomerView.js";
import { useAddComment, useAssignableSearch } from "../../hooks/useCustomerViewActions.js";
import { writeErrorText } from "../../lib/customerView/jiraValidation.js";
import { activeMention, insertMention, mentionsInText } from "../../lib/customerView/mentions.js";
import { ReconnectBanner } from "./ReconnectBanner.js";

/** Ab so vielen Zeichen nach dem „@" wird gesucht — dieselbe Schwelle wie `useAssignableSearch`. */
const MIN_QUERY = 2;

/**
 * Kommentar unter der Identität des Leads. Standard ist „an den Kunden" —
 * im Termin ist das der häufige Fall; intern ist die bewusste Ausnahme.
 *
 * Kompakt im Ruhezustand (eine Zeile) und mitwachsend beim Tippen: Das
 * Formular steht als Fußzeile im Detailbereich (s. `TicketDetail`) und nimmt
 * dort dauerhaft Platz weg, den es nur beim Schreiben braucht.
 *
 * **@-Erwähnungen (#275):** Wer „@" und zwei Buchstaben tippt, bekommt
 * Personen aus der Bearbeiter-Suche des Tickets vorgeschlagen (Pfeile,
 * Enter/Tab, Esc). Die Auswahl setzt „@Anzeigename" in den Text und merkt
 * sich die Account-Id; der Server macht daraus eine echte Jira-Erwähnung.
 * Die Vorschläge stehen ÜBER dem Feld — es klebt am unteren Rand seiner
 * Spalte, eine Liste darunter läge außerhalb des Sichtbaren.
 */
export function CommentComposer({ ticketKey, objectKey }: { ticketKey: string; objectKey: string | null }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState<CommentVisibility>("public");
  const [caret, setCaret] = useState(0);
  const [chosen, setChosen] = useState<CommentMention[]>([]);
  const [highlight, setHighlight] = useState(0);
  /** Mit Esc geschlossene Erwähnung (Position ihres „@") — bleibt zu, bis ein neues „@" beginnt. */
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const add = useAddComment(objectKey);

  const active = activeMention(
    text,
    caret,
    chosen.map((m) => m.displayName),
  );
  const open = active !== null && active.start !== dismissedAt;
  const searching = open && active.query.trim().length >= MIN_QUERY;
  const search = useAssignableSearch(searching ? ticketKey : null, active?.query ?? "");
  const users = searching ? search.data?.users ?? [] : [];

  const pick = (u: AssignableUser) => {
    if (!active) return;
    const next = insertMention(text, active, caret, u.displayName);
    setText(next.text);
    setCaret(next.caret);
    setChosen((c) => [...c, { accountId: u.accountId, displayName: u.displayName }]);
    setHighlight(0);
    // Erst nach dem Rendern des neuen Werts lässt sich der Cursor setzen.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  const submit = () => {
    if (!text.trim() || add.isPending) return;
    const body = text.trim();
    add.mutate(
      { key: ticketKey, text: body, visibility, mentions: mentionsInText(body, chosen) },
      {
        onSuccess: () => {
          setText("");
          setCaret(0);
          setChosen([]);
          setDismissedAt(null);
          setVisibility("public");
          notifications.show({ message: t("pages.customerView.cockpit.actions.commentPosted"), color: "green" });
        },
      },
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      submit();
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setDismissedAt(active.start);
      return;
    }
    if (users.length === 0) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setHighlight((h) => (h + step + users.length) % users.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pick(users[Math.min(highlight, users.length - 1)]!);
    }
  };

  const syncCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart ?? el.value.length);

  return (
    <Stack gap="xs">
      {open && (
        <Paper withBorder p={4} role="listbox" aria-label={t("pages.customerView.cockpit.actions.mentionList")}>
          {!searching ? (
            <Text size="xs" c="dimmed" px={4}>{t("pages.customerView.cockpit.actions.mentionHint")}</Text>
          ) : search.isLoading ? (
            <Loader size="xs" m={4} />
          ) : users.length === 0 ? (
            <Text size="xs" c="dimmed" px={4}>{t("pages.customerView.cockpit.actions.mentionNone")}</Text>
          ) : (
            users.map((u, i) => (
              <UnstyledButton
                key={u.accountId}
                role="option"
                aria-selected={i === highlight}
                display="block"
                w="100%"
                px={6}
                py={2}
                style={{ borderRadius: 4, background: i === highlight ? "var(--mantine-color-blue-light)" : undefined }}
                // Fokus bleibt im Textfeld — sonst wäre der Cursor weg.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(u)}
              >
                <Text size="sm">{u.displayName}</Text>
              </UnstyledButton>
            ))
          )}
        </Paper>
      )}
      <Textarea
        ref={inputRef}
        autosize
        minRows={1}
        maxRows={6}
        placeholder={t("pages.customerView.cockpit.actions.commentPlaceholder")}
        aria-label={t("pages.customerView.cockpit.actions.commentPlaceholder")}
        value={text}
        onChange={(e) => {
          setText(e.currentTarget.value);
          syncCaret(e.currentTarget);
          setHighlight(0);
        }}
        onSelect={(e) => syncCaret(e.currentTarget)}
        onKeyDown={onKeyDown}
      />
      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={visibility}
          onChange={(v) => setVisibility(v as CommentVisibility)}
          data={[
            { value: "public", label: t("pages.customerView.cockpit.actions.visibilityPublic") },
            { value: "internal", label: t("pages.customerView.cockpit.actions.visibilityInternal") },
          ]}
        />
        <Button size="xs" onClick={submit} loading={add.isPending} disabled={!text.trim()}>
          {t("pages.customerView.cockpit.actions.postComment")}
        </Button>
      </Group>
      {add.isError && (
        isJiraReauthRequired(add.error) ? <ReconnectBanner /> : (
          <Alert color="red" variant="light">
            {writeErrorText(add.error, t, "pages.customerView.cockpit.actions.commentFailed")}
          </Alert>
        )
      )}
    </Stack>
  );
}
